import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Service, ServiceStatus, CreateServiceRequest, UpdateServiceRequest, SERVICE_ROOMS, ServiceLine, SERVICE_STATUS_SELLING, SERVICE_STATUS_OPTIONS } from '@/types/service';
import { Button, TabContainer, TabItem } from '@/components/common';
import { categoryApi } from '@/api/endpoints/categories';
import { materialApi } from '@/api/endpoints/materials';
import { Category } from '@/types/category';
import { Material } from '@/types/material';
import { HiTrash } from 'react-icons/hi';
import { FormattedNumberInput } from '@/utils/number-format';
import { QUANTITY_DECIMAL_PLACES } from '@/constants/number';
import { toast } from '@/store/toastStore';

// Extended type for service lines in the form with additional tracking properties
type FormServiceLine = ServiceLine & { _isOriginal?: boolean };

interface ServiceFormProps {
  service?: Service;
  onSubmit: (data: CreateServiceRequest | UpdateServiceRequest, serviceLines?: ServiceLine[]) => void;
  onCancel: () => void;
  onSaveAndClose?: (data: CreateServiceRequest | UpdateServiceRequest, serviceLines?: ServiceLine[]) => void;
  loading?: boolean;
}

export const ServiceForm: React.FC<ServiceFormProps> = ({
  service,
  onSubmit,
  onCancel,
  onSaveAndClose,
  loading = false,
}) => {
  const { t } = useTranslation();
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<CreateServiceRequest>({
    name: '',
    categoryId: '',
    price: 0,
    unit: '',
    status: SERVICE_STATUS_SELLING,
    room: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [serviceLines, setServiceLines] = useState<FormServiceLine[]>([]);
  const [activeTab, setActiveTab] = useState('info');

  // Material search state
  const [materialSearchQuery, setMaterialSearchQuery] = useState('');
  const [materialSearchResults, setMaterialSearchResults] = useState<Material[]>([]);
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);
  const [searchingMaterials, setSearchingMaterials] = useState(false);

  // Refs
  const materialSearchRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load categories and materials on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingCategories(true);

        const [categoriesResponse, materialsResponse] = await Promise.all([
          categoryApi.getCategories(1, 100, { type: ['service_type'] }),
          materialApi.getMaterials(1, 100, { status: ['active'] })
        ]);

        setCategories(categoriesResponse.data);
        setMaterials(materialsResponse.data);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadData();
  }, []);

  // Handle click outside to close material dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (materialSearchRef.current && !materialSearchRef.current.contains(event.target as Node)) {
        setShowMaterialDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup timeouts
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (service) {
      setFormData({
        name: service.name,
        categoryId: service.categoryId,
        price: service.price,
        unit: service.unit || '',
        status: service.status,
        room: service.room,
      });

      // Load service lines if available - but mark them as original to distinguish from new ones
      if (service.serviceLines) {
        const originalServiceLines: FormServiceLine[] = service.serviceLines.map(line => ({
          ...line,
          _isOriginal: true // Mark as original service line
        }));
        setServiceLines(originalServiceLines);
      }
    } else {
      // Reset form when no service is provided (create mode)
      setFormData({
        name: '',
        categoryId: '',
        price: 0,
        unit: '',
        status: SERVICE_STATUS_SELLING,
        room: '',
      });
      setServiceLines([]);
      setActiveTab('info');
    }
  }, [service]);

  // Load missing materials when service is loaded for editing
  useEffect(() => {
    const loadMissingMaterials = async () => {
      if (!service?.serviceLines || service.serviceLines.length === 0) {
        return;
      }

      // Extract unique material codes from service lines
      const serviceLineMaterialCodes = service.serviceLines
        .map(line => line.materialId)
        .filter((code): code is string => !!code);

      if (serviceLineMaterialCodes.length === 0) {
        return;
      }

      // Find which materials are missing from the loaded materials array
      const existingMaterialCodes = new Set(materials.map(m => m.code));
      const missingMaterialCodes = serviceLineMaterialCodes.filter(
        code => !existingMaterialCodes.has(code)
      );

      if (missingMaterialCodes.length > 0) {
        try {
          // Fetch missing materials using the bulk endpoint
          const missingMaterials = await materialApi.getMaterialsByCodes(missingMaterialCodes);
          if (missingMaterials.length > 0) {
            setMaterials(prev => {
              // Avoid duplicates
              const existingCodes = new Set(prev.map(m => m.code));
              const newMaterials = missingMaterials.filter(m => !existingCodes.has(m.code));
              return [...prev, ...newMaterials];
            });

            // Update service lines with material names
            setServiceLines(prev => prev.map(line => {
              if (!line.materialId) return line;
              const material = missingMaterials.find(m => m.code === line.materialId);
              if (material && !line.materialName) {
                return { ...line, materialName: material.name };
              }
              return line;
            }));
          }
        } catch (error) {
          console.error('Error loading missing materials:', error);
          // Don't block the UI if material loading fails
        }
      } else {
        // All materials are already loaded, just populate materialName if missing
        setServiceLines(prev => prev.map(line => {
          if (!line.materialId || line.materialName) return line;
          const material = materials.find(m => m.code === line.materialId);
          if (material) {
            return { ...line, materialName: material.name };
          }
          return line;
        }));
      }
    };

    loadMissingMaterials();
  }, [service, materials]);

  // Focus on name input when modal opens
  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  // Helper function to handle validation errors
  const handleValidationErrors = () => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const errorElement = document.getElementById(firstErrorField);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('services.validation.nameRequired');
    }

    if (!formData.categoryId) {
      newErrors.categoryId = t('services.validation.categoryRequired');
    }

    if (formData.price <= 0) {
      newErrors.price = t('services.validation.priceRequired');
    }

    if (!formData.unit || !formData.unit.trim()) {
      newErrors.unit = t('services.validation.unitRequired');
    }

    if (!formData.room.trim()) {
      newErrors.room = t('services.validation.roomRequired');
    }

    setErrors(newErrors);
    
    // If there are errors, show a general error message
    if (Object.keys(newErrors).length > 0) {
      toast.error('Validation Error', t('services.validation.pleaseFillRequiredFields'));
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Filter service lines to only include those with selected materials
      const validServiceLines = serviceLines.filter(line => line.materialId);

      if (service) {
        onSubmit({ id: service.id, ...formData } as UpdateServiceRequest, validServiceLines);
      } else {
        onSubmit(formData, validServiceLines);
      }
    } else {
      // Show validation errors and scroll to first error field
      handleValidationErrors();
    }
  };

  const handleSaveAndClose = () => {
    if (validateForm()) {
      // Filter service lines to only include those with selected materials
      const validServiceLines = serviceLines.filter(line => line.materialId);

      if (service) {
        onSaveAndClose?.({ id: service.id, ...formData } as UpdateServiceRequest, validServiceLines);
      } else {
        onSaveAndClose?.(formData, validServiceLines);
      }
    } else {
      // Show validation errors and scroll to first error field
      handleValidationErrors();
    }
  };

  const handleInputChange = (field: keyof CreateServiceRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Material search functionality
  const searchMaterials = async (query: string = '') => {
    setSearchingMaterials(true);
    try {
      const response = await materialApi.getMaterials(1, 20, {
        search: query || undefined, // Allow empty search to get all materials
        status: ['active'],
        type: ['raw', 'semi-product'] // Include both raw and semi-finished products
      });
      setMaterialSearchResults(response.data);
      setShowMaterialDropdown(response.data.length > 0);
    } catch (error) {
      console.error('Error searching materials:', error);
      setMaterialSearchResults([]);
      setShowMaterialDropdown(false);
    } finally {
      setSearchingMaterials(false);
    }
  };

  const handleMaterialSearch = (query: string) => {
    setMaterialSearchQuery(query);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search with 300ms delay
    searchTimeoutRef.current = setTimeout(() => {
      searchMaterials(query);
    }, 300);
  };

  // Preload materials on hover/focus with debouncing
  const preloadMaterials = () => {
    if (materialSearchResults.length === 0 && !searchingMaterials) {
      searchMaterials(''); // Empty search to get all active raw materials
    }
  };

  // Handle mouse enter - preload data with debouncing
  const handleMaterialInputMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      preloadMaterials();
    }, 200); // 200ms debounce
  };

  // Handle mouse leave - clear timeout
  const handleMaterialInputMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Handle focus - preload materials immediately
  const handleMaterialInputFocus = () => {
    preloadMaterials();
  };

  const selectMaterial = (material: Material) => {
    // Check if material is already added
    const existingLine = serviceLines.find(line => line.materialId === material.code);
    if (existingLine) {
      // Focus on existing line or show message
      return;
    }

    // Add new service line with selected material
    const newServiceLine: FormServiceLine = {
      id: `temp-${Date.now()}`,
      code: '',
      serviceId: service?.id || '',
      materialId: material.code,
      materialName: material.name,
      quantity: 1,
      loseRate: 0, // Default lose rate is 1 (minimum value)
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      _isOriginal: false, // Mark as new service line
    };
    setServiceLines(prev => [...prev, newServiceLine]);

    // Clear search
    setMaterialSearchQuery('');
    setMaterialSearchResults([]);
    setShowMaterialDropdown(false);
  };

  // Service line management functions

  const removeServiceLine = (index: number) => {
    setServiceLines(prev => prev.filter((_, i) => i !== index));
  };

  const updateServiceLine = (index: number, field: keyof FormServiceLine, value: any) => {
    setServiceLines(prev => prev.map((line, i) => {
      if (i === index) {
        const updatedLine = { ...line, [field]: value };

        // If material is changed, update material name
        if (field === 'materialId') {
          const material = materials.find(m => m.code === value);
          updatedLine.materialName = material?.name || '';
        }

        return updatedLine;
      }
      return line;
    }));
  };

  // Create tab items
  const tabs: TabItem[] = [
    {
      id: 'info',
      label: t('services.tabs.info'),
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Service Name */}
            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                {t('services.labels.serviceNameLabel')}
              </label>
              <input
                ref={nameInputRef}
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={t('services.placeholders.serviceName')}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-2">
                {t('services.labels.serviceCategoryLabel')}
              </label>
              <select
                id="categoryId"
                value={formData.categoryId}
                onChange={(e) => handleInputChange('categoryId', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.categoryId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">{t('services.placeholders.selectCategory')}</option>
                {loadingCategories ? (
                  <option disabled>{t('services.labels.loading')}</option>
                ) : (
                  categories.map((category) => (
                    <option key={category.id} value={category.code}>
                      {category.name}
                    </option>
                  ))
                )}
              </select>
              {errors.categoryId && <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>}
            </div>

            {/* Price */}
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                {t('services.labels.priceLabel')}
              </label>
              <FormattedNumberInput
                id="price"
                value={formData.price}
                onChange={(value) => handleInputChange('price', value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.price ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={t('services.placeholders.enterPrice')}
              />
              {errors.price && <p className="mt-1 text-sm text-red-600">{errors.price}</p>}
            </div>

            {/* Unit */}
            <div>
              <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-2">
                {t('services.fields.unit')} <span className="text-gray-900">*</span>
              </label>
              <input
                type="text"
                id="unit"
                value={formData.unit || ''}
                onChange={(e) => handleInputChange('unit', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.unit ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="cuốn, cái, giờ, lượt, km, m2, ..."
              />
              {errors.unit && <p className="mt-1 text-sm text-red-600">{errors.unit}</p>}
            </div>

            {/* Room */}
            <div>
              <label htmlFor="room" className="block text-sm font-medium text-gray-700 mb-2">
                {t('services.labels.executionRoomLabel')}
              </label>
              <select
                id="room"
                value={formData.room}
                onChange={(e) => handleInputChange('room', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                  errors.room ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">{t('services.placeholders.selectRoom')}</option>
                {SERVICE_ROOMS.map((room) => (
                  <option key={room} value={room}>
                    {t(`services.rooms.${room.toLowerCase().replace(/\s+/g, '_')}`)}
                  </option>
                ))}
              </select>
              {errors.room && <p className="mt-1 text-sm text-red-600">{errors.room}</p>}
            </div>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                {t('services.labels.statusLabel')}
              </label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value as ServiceStatus)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              >
                {SERVICE_STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {t(status.label)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'materials',
      label: t('services.tabs.materials'),
      content: (
        <div className="space-y-6">
          {/* Materials Header */}
          <div>
            <h3 className="text-lg font-medium text-gray-900">{t('services.fields.serviceLines')}</h3>
          </div>

          {/* Material Search */}
          <div className="relative" ref={materialSearchRef}>
            <div className="flex items-center space-x-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={materialSearchQuery}
                  onChange={(e) => handleMaterialSearch(e.target.value)}
                  onMouseEnter={handleMaterialInputMouseEnter}
                  onMouseLeave={handleMaterialInputMouseLeave}
                  onFocus={handleMaterialInputFocus}
                  placeholder={t('services.placeholders.searchMaterials')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
                {searchingMaterials && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-500"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Search Results Dropdown */}
            {showMaterialDropdown && materialSearchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {materialSearchResults.map((material) => (
                  <div
                    key={material.id}
                    onClick={() => selectMaterial(material)}
                    className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">{material.name}</div>
                        <div className="text-sm text-gray-500">
                          {material.code} • {material.unit}
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        {material.actualQuantity} {material.unit}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* No results message */}
            {showMaterialDropdown && materialSearchResults.length === 0 && materialSearchQuery && !searchingMaterials && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4 text-center text-gray-500">
                {t('services.labels.noMaterialsFound')}
              </div>
            )}
          </div>

          {/* Materials Table */}
          {serviceLines.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      STT
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('services.fields.code')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('services.labels.materialName')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('services.labels.quantity')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('services.labels.loseRate')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('services.labels.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {serviceLines.map((line, index) => {
                    const material = materials.find(m => m.code === line.materialId);
                    return (
                      <tr key={line.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={line.materialId}
                            onChange={(e) => updateServiceLine(index, 'materialId', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          >
                            <option value="">{t('services.placeholders.selectMaterial')}</option>
                            {materials.map((material) => (
                              <option key={material.id} value={material.code}>
                                {material.code}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {material?.name || line.materialName || ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <FormattedNumberInput
                            value={line.quantity}
                            onChange={(value) => updateServiceLine(index, 'quantity', value)}
                            maxDecimalPlaces={QUANTITY_DECIMAL_PLACES}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <FormattedNumberInput
                            value={line.loseRate}
                            decimalPlaces={0}
                            onChange={(value) => updateServiceLine(index, 'loseRate', Math.max(0, Math.min(100, Math.round(value))))}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            icon={<HiTrash className="h-4 w-4" />}
                            onClick={() => removeServiceLine(index)}
                            className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>{t('services.labels.noMaterials')}</p>
              <p className="text-sm">{t('services.labels.noMaterialsDescription')}</p>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Tab Container */}
      <TabContainer
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          {t('services.actions.cancel')}
        </Button>
        {onSaveAndClose && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveAndClose}
            disabled={loading}
          >
            {t('services.actions.saveAndClose')}
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          loading={loading}
        >
          {service ? t('services.actions.update') : t('services.actions.create')}
        </Button>
      </div>
    </form>
  );
};
